// External libraries
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { useQuery } from '@tanstack/react-query';
import { IoIosArrowDown } from "react-icons/io";

// Components
import { GEO_LEVELS, RouteConstants } from "../../constants";
import { Body, BodyColor, BodyType, Heading, Search, TypographyColor, TypographyType } from "../../ui-elements";

// Utilities
import { cifTypeState, compareListState, dropdownList, geoState, loggedUserState, selectedGeoDropdownState, selectedGeoNameState, selPOICategories, userCurrencyState } from "../../states";
import { createSearchParams, getCurrentGeo, getSelectedGeoName, setDocumentTitle, useMapHelpers } from "../../helpers";
import { useCompareService, useMapsService } from "../../services";
import { AddToCompare } from "../home";

export const GeoSearch = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const mapServices = useMapsService();
    const compareService = useCompareService();
    const { getCurrencyWithSymbol } = useMapHelpers();
    const selCurrency = useRecoilValue(userCurrencyState);
    const selCategoryList = useRecoilValue(selPOICategories);
    const cifType = useRecoilValue(cifTypeState);
    const compareList = useRecoilValue(compareListState);
    const loggedUser = useRecoilValue(loggedUserState);
    const [searchParams, setSearchParams] = useSearchParams();
    const [geoDropdownList, setGeoDropdownList] = useRecoilState(dropdownList);
    const [selectedGeo, setselectedGeo] = useRecoilState(geoState);
    const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [selectedGeoName, setSelectedGeoName] = useRecoilState(selectedGeoNameState);
    const [selectedItem, setSelectedItem] = useState<string>('');
    const setSelectedGeoDropdown = useSetRecoilState(selectedGeoDropdownState);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setSelectedItem(loggedUser?.sublevel === 'subdistrict' ? 'Sub-District' : 'Pincode');
    }, [loggedUser]);

    const handleDropdownToggle = (index: number) => {
        if (index !== 0) {
            setActiveDropdown((prev) => (index));
        }
    };

    const handleNavigateBack = (list: any, geoId: any, curr_level: string, next_geo_level: any, index: number, event: any) => {
        if (!(event.target as HTMLElement).parentElement?.id.includes('geo-navigate-btn')) return;
        if (!list || !geoId) return;

        if (geoId === geoCode) return;

        const foundItem = list.find((item: any) => item.geoId === Number(geoId));
        handleGeoSelect(foundItem.geoId, curr_level, next_geo_level, index, foundItem.geoName, foundItem.count);
    }

    const handleInputChange = (value: string) => {
        setSearchTerm(value.toLowerCase());
    };

    const handleGeoSelect = (geo_id: number, curr_level: string, next_geo_level: any, index: number, geoName: string, poiCount?: string) => {
        setSelectedGeoName(geoName);
        const resetFields: any = {
            country: { state: '', district: '', sub_district: '', village: '' },
            state: { district: '', sub_district: '', village: '' },
            district: { sub_district: '', village: '' },
            sub_district: { village: '' },
        };
        handleDropdownToggle(index);
        setSearchTerm('');
        if (curr_level == "pincode") {
            curr_level = "sub_district"
        }

        const updatedGeo = {
            ...selectedGeo,
            [curr_level]: geo_id,
            ...(resetFields[curr_level] || {}),
        }
        setselectedGeo(updatedGeo);
        setTimeout(() => {
            const newSearchParams = createSearchParams(updatedGeo);
            if (location.pathname === RouteConstants.no_poi) {
                navigate(RouteConstants.dashboard + "?" + newSearchParams);
            }
        }, 0);

    }

    const handleSearchParams = (updatedParams: Record<string, string | null>) => {
        const currentParams = new URLSearchParams(searchParams);
        Object.entries(updatedParams).forEach(([key, value]) => {
            if (value) {
                currentParams.set(key, value);
            } else {
                currentParams.delete(key);
            }
        });
        setSearchParams(currentParams);
    };

    const getFilteredData = (data: any[]) => {
        if (!searchTerm) return data;
        return data.filter((item) =>
            item.geoName.toLowerCase().includes(searchTerm)
        );
    };

    const getPayload = (geoId: number) => {
        return {
            geoId,
            type: cifType,
            currency: selCurrency,
            ...selCategoryList,
        }
    }

    const geoCode = useMemo(() => {
        const { country, state, district, sub_district, village } = selectedGeo;
        const geo_id = village || sub_district || district || state || country;
        return Number(geo_id);
    }, [selectedGeo]);

    const updateGeoDropdownList = () => {
        setGeoDropdownList((prev) => {
            const updatedList = { ...prev };

            if (geoCode === Number(selectedGeo.country)) {
                updatedList.districts = null;
                updatedList.sub_districts = null;
                updatedList.villages = null;
            } else if (geoCode === Number(selectedGeo.state)) {
                updatedList.sub_districts = null;
                updatedList.villages = null;
            } else if (geoCode === Number(selectedGeo.district)) {
                updatedList.villages = null;
            }
            return updatedList;
        });
    };

    const updateSelGeoName = () => {
        const { country, state, district, sub_district, village } = selectedGeo;
        let geoList: any = [];

        if (village) {
            geoList = geoDropdownList['villages']?.dropDownData || [];
        } else if (sub_district) {
            geoList = geoDropdownList['sub_districts']?.dropDownData || [];
        } else if (district) {
            geoList = geoDropdownList['districts']?.dropDownData || [];
        } else if (state) {
            geoList = geoDropdownList['states']?.dropDownData || [];
        } else if (country) {
            geoList = geoDropdownList['countries']?.dropDownData || [];
        }

        const found = geoList.find((item: any) => item.geoId === Number(geoCode));
        if (found) {
            setSelectedGeoName(found.geoName);
        }
    };

    const isEqual = (obj1: any, obj2: any) => {
        const normalize = (value: any) => (!value ? null : String(value));

        const keys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);

        for (const key of keys) {
            if (normalize(obj1[key]) !== normalize(obj2[key])) {
                return false;
            }
        }
        return true;
    };

    const handleAddtoCompare = async (e: any, item: any) => {
        e.stopPropagation();
        if (!item.addToCompare) {
            const payload = {
                addToCompare: [{
                    geoId: item.geoId, level: item.geoLevelNumber,
                    ...(item.geotype === "pincode" && { parent_id: Number(selectedGeo.district) })
                }]
            }
            await compareService.postCompreList(payload);
        } else {
            navigate(RouteConstants.compare);
        }
    }

    useEffect(() => {
        const currSearchParam = {
            country: searchParams.get("country") || "1",
            state: searchParams.get("state") || "",
            district: searchParams.get("district") || "",
            sub_district: searchParams.get("sub_district") || "",
            village: searchParams.get("village") || "",
        };

        const updatedParams: Record<string, string | null> = {
            country: selectedGeo.country || null,
            state: selectedGeo.state || null,
            district: selectedGeo.district || null,
            sub_district: selectedGeo.sub_district || null,
            village: selectedGeo.village || null,
        };

        if (!isEqual(updatedParams, currSearchParam)) {
            setselectedGeo((prev: any) => ({ ...currSearchParam }));
        }
    }, [searchParams]);


    useEffect(() => {
        if (selectedGeo) {
            const updatedParams: Record<string, string | null> = {
                country: selectedGeo.country || null,
                state: selectedGeo.state || null,
                district: selectedGeo.district || null,
                sub_district: selectedGeo.sub_district || null,
                village: selectedGeo.village || null,
            };

            const currSearchParam = {
                country: searchParams.get("country") || "1",
                state: searchParams.get("state") || null,
                district: searchParams.get("district") || null,
                sub_district: searchParams.get("sub_district") || null,
                village: searchParams.get("village") || null,
            };

            if (!isEqual(updatedParams, currSearchParam)) {
                handleSearchParams(updatedParams);
                updateSelGeoName();
            }
        }
    }, [selectedGeo]);

    // Memoized payloads for each geo level
    const statesPayload = useMemo(() => {
        if (selectedGeo?.country && selCategoryList?.category?.length > 0 && selCategoryList?.subcategory?.length > 0) {
            return getPayload(Number(selectedGeo.country));
        }
        return null;
    }, [selectedGeo?.country, selCategoryList, cifType, selCurrency]);

    const districtsPayload = useMemo(() => {
        if (selectedGeo?.state && selCategoryList?.category?.length > 0 && selCategoryList?.subcategory?.length > 0) {
            return getPayload(Number(selectedGeo.state));
        }
        return null;
    }, [selectedGeo?.state, selCategoryList, cifType, selCurrency]);

    const subDistrictsPayload = useMemo(() => {
        if (selectedGeo?.district && selCategoryList?.category?.length > 0 && selCategoryList?.subcategory?.length > 0) {
            return getPayload(Number(selectedGeo.district));
        }
        return null;
    }, [selectedGeo?.district, selCategoryList, cifType, selCurrency]);

    const villagesPayload = useMemo(() => {
        if (selectedGeo?.sub_district && selCategoryList?.category?.length > 0 && selCategoryList?.subcategory?.length > 0) {
            return getPayload(Number(selectedGeo.sub_district));
        }
        return null;
    }, [selectedGeo?.sub_district, selCategoryList, cifType, selCurrency]);

    // TanStack Query hooks for each geo level
    const { data: statesData } = useQuery({
        queryKey: ['geoDropdown', 'states', statesPayload],
        queryFn: async () => {
            if (!statesPayload) return null;
            return await mapServices.getGeoDropdown(statesPayload);
        },
        enabled: !!statesPayload,
        staleTime: 5 * 60 * 1000,
    });

    const { data: districtsData } = useQuery({
        queryKey: ['geoDropdown', 'districts', districtsPayload],
        queryFn: async () => {
            if (!districtsPayload) return null;
            return await mapServices.getGeoDropdown(districtsPayload);
        },
        enabled: !!districtsPayload,
        staleTime: 5 * 60 * 1000,
    });

    const { data: subDistrictsData } = useQuery({
        queryKey: ['geoDropdown', 'sub_districts', subDistrictsPayload],
        queryFn: async () => {
            if (!subDistrictsPayload) return null;
            return await mapServices.getGeoDropdown(subDistrictsPayload);
        },
        enabled: !!subDistrictsPayload,
        staleTime: 5 * 60 * 1000,
    });

    const { data: villagesData } = useQuery({
        queryKey: ['geoDropdown', 'villages', villagesPayload],
        queryFn: async () => {
            if (!villagesPayload) return null;
            return await mapServices.getGeoDropdownForVillage(villagesPayload);
        },
        enabled: !!villagesPayload,
        staleTime: 5 * 60 * 1000,
    });

    // Update dropdown list when query data changes
    useEffect(() => {
        if (statesData) {
            console.log("HHH")
            setGeoDropdownList(prev => ({ ...prev, states: statesData }));
        }
    }, [statesData]);

    useEffect(() => {
        if (districtsData) {
            setGeoDropdownList(prev => ({ ...prev, districts: districtsData }));
        }
    }, [districtsData]);

    useEffect(() => {
        if (subDistrictsData) {
            setGeoDropdownList(prev => ({ ...prev, sub_districts: subDistrictsData }));
        }
    }, [subDistrictsData]);

    useEffect(() => {
        if (villagesData) {
            setGeoDropdownList(prev => ({ ...prev, villages: villagesData }));
        }
    }, [villagesData]);

    useEffect(() => {
        updateGeoDropdownList();
    }, [geoCode]);

    useEffect(() => {
        setDocumentTitle(selectedGeoName);
    }, [selectedGeoName]);

    useEffect(() => {
        if (inputRef.current && activeDropdown) {
            inputRef.current.focus();
        }
    }, [activeDropdown]);

    useEffect(() => {
        if (geoDropdownList && selectedGeo && geoCode) {
            const catchment = searchParams.get("catchment");
            if (catchment) return;
            const selGeo = getCurrentGeo(geoDropdownList, geoCode, selectedGeo);
            setSelectedGeoName(selGeo?.geoName || 'India');
            setSelectedGeoDropdown(selGeo);
        }
    }, [selectedGeo, geoDropdownList]);

    return (
        <div className="col-8 row d-flex justify-content-start align-items-center h-100 position-relative">
            {GEO_LEVELS.map((geo_level, index) => {
                // Skip rendering country level
                if (geo_level.key === 'country') {
                    return null;
                }
                if (cifType.toString() === 'CTV' && geo_level.key === 'sub_district' && location.pathname !== RouteConstants.dashboard) {
                    return null; // Skip rendering
                }
                const geoList = geoDropdownList[geo_level.list as keyof typeof geoDropdownList];
                // old code before pin code
                // const filteredData = getFilteredData(geoList?.dropDownData || []);

                // const compareGeoIds = [
                //     ...(compareList?.districts || []).map((item: any) => item.geoId),
                //     ...(compareList?.subdistricts || []).map((item: any) => item.geoId)
                // ];

                // const updatedFilterData = filteredData.map((item: any) => ({
                //     ...item,
                //     addToCompare: compareGeoIds.includes(item.geoId)
                // }));
                // new code
                const filteredData = getFilteredData(geoList?.dropDownData || []);

                const compareGeoIds = [
                    ...(compareList?.districts || []).map((item: any) => item.geoId),
                    ...(compareList?.subdistricts || []).map((item: any) => item.geoId),
                    ...(compareList?.pincodes || []).map((item: any) => item.geoId),
                    ...(compareList?.villages || []).map((item: any) => item.geoId),
                ];

                let updatedFilterData = filteredData;
                // 🔍 Filter only for sub_district level
                if (geo_level.key === 'sub_district' || geo_level.key === 'pincode') {
                    updatedFilterData = filteredData.filter((item: any) =>
                        selectedItem === 'Sub-District'
                            ? item.geotype === 'sub_district'
                            : item.geotype === 'pincode'
                    );
                }
                // 💡 Then add `addToCompare` flag
                updatedFilterData = updatedFilterData.map((item: any) => ({
                    ...item,
                    addToCompare: compareGeoIds.includes(item.geoId),
                }));
                return (
                    <div
                        key={geo_level.key}
                        className={`d-flex flex-column justify-content-end align-items-center ${index === 0 ? 'col-1 p-0' : index >= 3 ? 'col-3 justify-content-between' : 'col-3 justify-content-between'} h-100 position-relative`}
                        onMouseEnter={() => handleDropdownToggle(index)}
                        onMouseLeave={() => { setActiveDropdown(null); setSearchTerm('') }}
                        onClick={(event) =>
                            handleNavigateBack(
                                geoList?.dropDownData,
                                selectedGeo[geo_level.key as keyof typeof selectedGeo],
                                geo_level.key,
                                GEO_LEVELS[index + 1],
                                index,
                                event
                            )
                        }
                    >
                        <div
                            id={`geo-navigate-btn-${index}`}
                            className={`px-2 d-flex justify-content-between align-items-center w-100 rounded-1 ${geoList?.dropDownData && geoList?.dropDownData.length > 0 ? 'primary-hover' : ''}`}
                            style={{ height: '5vh', marginTop: '0.908vh' }}
                        >
                            <Heading
                                title={getSelectedGeoName(geoList?.dropDownData, selectedGeo[geo_level.key as keyof typeof selectedGeo], geo_level.key)}
                                type={TypographyType.h5}
                                colour={geoList?.dropDownData && geoList?.dropDownData.length > 0 ? getSelectedGeoName(geoList?.dropDownData, selectedGeo[geo_level.key as keyof typeof selectedGeo], geo_level.key) === selectedGeoName ? TypographyColor.primary : TypographyColor.secondary : TypographyColor.muted}
                                classname={`text-start text-truncate ${geoList?.dropDownData && geoList?.dropDownData.length > 0 ? 'cursor-pointer secondary-hover' : 'cursor-default'}`}
                            />
                            {geoList?.dropDownData && <IoIosArrowDown color="black" className="flex-shrink-0 fs-12" />}
                        </div>
                        <hr className={`m-0 w-100 ${geoCode === Number(selectedGeo[geo_level.key as keyof typeof selectedGeo]) ? 'selected-geo' : 'non-selected-geo'}`} />
                        {geoDropdownList && geoList?.dropDownData && geoList?.dropDownData?.length > 0 && activeDropdown === index && index !== 0 && (
                            <div
                                className="dropdown-content overflow-hidden"
                            // onMouseLeave={() => setActiveDropdown(null)}
                            >
                                <Search
                                    handleInputChange={handleInputChange}
                                    searchTerm={searchTerm}
                                    hideSuggestionBox={true}
                                    placeholderValue='Search'
                                    classname='map-filters w-100 border'
                                    ref={inputRef}
                                />
                                {/* heading for sub district/pincode tab */}
                                {geo_level.key === 'sub_district' && <div style={{ height: '4vh' }} className="margin-top-bottom-3 d-flex flex-row justify-content-start align-items-center bottom-border">
                                    {['Sub-District', 'Pincode'].map((item, index) => {
                                        return <div className={`h-100 d-flex flex-column justify-content-between align-items-center margin-right-3 cursor-pointer`} onClick={() => setSelectedItem(item)}>
                                            <Body
                                                type={BodyType.p2}
                                                color={BodyColor.secondary}
                                                classname="padding-left-right-2"
                                            >
                                                {item}
                                            </Body>
                                            <hr className={`m-0 w-100 ${selectedItem === item ? 'selected-geo' : 'non-selected-geo'}`} />
                                        </div>
                                    })}
                                </div>}

                                <div className="table-container no-scrollbar">
                                    <table className="padding-top-3">
                                        <thead className="">
                                            <tr>
                                                <th>
                                                    <Body
                                                        type={BodyType.p2}
                                                        color={BodyColor.secondary}
                                                        classname="padding-top-bottom-3 padding-right-3"
                                                    >
                                                        {/* {`${geo_level.name} (${updatedFilterData?.length || 0})`} */}
                                                        {geo_level.key === 'sub_district' && selectedItem === 'Pincode'
                                                            ? `Pincodes (${updatedFilterData?.length || 0})`
                                                            : `${geo_level.name} (${updatedFilterData?.length || 0})`}
                                                    </Body>
                                                </th>
                                                {geo_level.key == 'state' && cifType.toString() === 'PoI' &&
                                                    <th>
                                                        <Body
                                                            type={BodyType.p2}
                                                            color={BodyColor.secondary}
                                                            classname="padding-top-bottom-3 padding-right-3"
                                                        >
                                                            {`District Coverage(${geoList?.totalDistrictCoverage || 0})`}
                                                        </Body>
                                                    </th>
                                                }
                                                {cifType.toString() === 'PoI' &&
                                                    <th>
                                                        <Body
                                                            type={BodyType.p2}
                                                            color={BodyColor.secondary}
                                                            classname="padding-top-bottom-3 padding-right-3"
                                                        >
                                                            {geo_level.key === 'sub_district' && selectedItem === 'Pincode'
                                                                ? `PoI Count (${geoList?.totalPoiCount?.pincode || 0})`
                                                                : geo_level.key === 'sub_district' && selectedItem === 'Sub-District'
                                                                    ? `PoI Count (${geoList?.totalPoiCount?.subdistrict || 0})`
                                                                    : `PoI Count (${(geoList?.totalPoiCount as any)?.[geo_level?.key] || 0})`}
                                                        </Body>
                                                    </th>
                                                }
                                                {cifType.toString() === 'EH' &&
                                                    <th>
                                                        <Body
                                                            type={BodyType.p2}
                                                            color={BodyColor.secondary}
                                                            classname="padding-top-bottom-3 padding-right-3"
                                                        >
                                                            {`EH (${geoList?.totalEhCount || 0})`}
                                                        </Body>
                                                    </th>
                                                }
                                                {cifType.toString() === 'CTV' &&
                                                    <th>
                                                        <Body
                                                            type={BodyType.p2}
                                                            color={BodyColor.secondary}
                                                            classname="padding-top-bottom-3 padding-right-3"
                                                        >
                                                            {`CTV (${getCurrencyWithSymbol(geoList?.totalCtvCount, geoList.currency)})`}
                                                        </Body>
                                                    </th>
                                                }
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {updatedFilterData?.map(item => (

                                                <tr
                                                    key={item.geoId}
                                                    onClick={() => handleGeoSelect(item.geoId, geo_level.key, GEO_LEVELS[index + 1], index, item.geoName, item.count)}
                                                    className="poi-category padding-top-bottom-3 cursor-pointer district-item"
                                                >
                                                    <td className="paddding-left-right-0 min-w-200px">
                                                        <Heading
                                                            title={item.geoName}
                                                            type={TypographyType.h5}
                                                            colour={TypographyColor.secondary}
                                                            classname='text-start padding-left-right-3 secondary-hover'
                                                        />
                                                    </td>

                                                    {geo_level.key === 'state' && cifType.toString() === 'PoI' && (
                                                        <td className="paddding-left-right-0">
                                                            <Heading
                                                                title={item.coverage || '--'}
                                                                type={TypographyType.h5}
                                                                colour={TypographyColor.secondary}
                                                                classname='text-start padding-left-right-3 secondary-hover'
                                                            />
                                                        </td>
                                                    )}

                                                    {cifType.toString() === 'PoI' && (
                                                        <td className="paddding-left-right-0">
                                                            <Heading
                                                                title={item.poicount || '--'}
                                                                type={TypographyType.h5}
                                                                colour={TypographyColor.secondary}
                                                                classname='text-start padding-left-right-3 secondary-hover'
                                                            />
                                                        </td>
                                                    )}

                                                    {cifType.toString() === 'CTV' && (
                                                        <td className="paddding-left-right-0">
                                                            <Heading
                                                                title={getCurrencyWithSymbol(item.ctvcount, geoList.currency)}
                                                                type={TypographyType.h5}
                                                                colour={TypographyColor.secondary}
                                                                classname='text-start padding-left-right-3 secondary-hover'
                                                            />
                                                        </td>
                                                    )}

                                                    {cifType.toString() === 'EH' && (
                                                        <td className="paddding-left-right-0">
                                                            <Heading
                                                                title={item.ehpopcount || '--'}
                                                                type={TypographyType.h5}
                                                                colour={TypographyColor.secondary}
                                                                classname='text-start padding-left-right-3 secondary-hover'
                                                            />
                                                        </td>
                                                    )}

                                                    {(item.geotype === "district" || item.geotype === 'sub_district' || item.geotype === "district" || item.geotype === 'pincode' || item.geotype === 'village') && (
                                                        <td className="padding-left-right-3">
                                                            <AddToCompare
                                                                isAddedToCompare={item.addToCompare}
                                                                geoId={item.geoId}
                                                                level={item.geoLevelNumber}
                                                                parent_id={Number(selectedGeo.district)}
                                                                count={item.geotype === "district"
                                                                    ? compareList?.districts?.length :
                                                                    item.geotype === "sub_district" ? compareList?.subdistricts?.length :
                                                                        item.geotype === "pincode" ? compareList?.pincodes?.length :
                                                                            compareList?.villages?.length}
                                                                tooltipPosition="left"
                                                            />
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}

                                        </tbody>
                                    </table>

                                    {!updatedFilterData || updatedFilterData.length === 0 &&
                                        <Heading
                                            title='No data found'
                                            type={TypographyType.h5}
                                            colour={TypographyColor.secondary}
                                            classname='text-center padding-2 w-100'
                                        />}
                                </div>
                            </div>
                        )}
                    </div>
                )
            })}
        </div>
    )
}